import { priceSimulationActionTypes as actionTypes } from '../ActionTypes/priceSimulationActionTypes';
import { roundPackagePrices } from '../Utils/priceSimulationUtils';
import type { components } from '../Types/api';

type Schemas = components['schemas'];
type PackageListItemDto = Schemas['Cockpit.Models.PriceSimulation.PackageListItemDto'];
type PackageSimulationDto = Schemas['Cockpit.Models.PriceSimulation.PackageSimulationDto'];
type ReviewerDto = Schemas['Cockpit.Models.PriceSimulation.ReviewerDto'];
type PriceSimulationPackageContextResponse = Schemas['Cockpit.Models.PriceSimulation.PriceSimulationPackageContextResponse'];

interface PackageMetadata {
    hasActiveProposal: boolean;
    isOutdatedVersion: boolean;
}

export interface PriceSimulationReducerState {
    packages: PackageListItemDto[] | null;
    packageDetailsMap: Record<string, PackageSimulationDto>;
    packageMetadataMap: Record<string, PackageMetadata>;
    reviewers: ReviewerDto[];
}

type PriceSimulationAction =
    | { type: typeof actionTypes.STORE_PRICE_SIMULATION_PACKAGES; data: PackageListItemDto[] }
    | { type: typeof actionTypes.STORE_PRICE_SIMULATION_PACKAGE_CONTEXTS; contexts: PriceSimulationPackageContextResponse[] }
    | { type: typeof actionTypes.SAVE_DRAFT_PROPOSALS; packages: { packageId: string }[] }
    | { type: typeof actionTypes.INVALIDATE_PACKAGE_CONTEXTS; packageIds: string[] }
    | { type: typeof actionTypes.STORE_REVIEWERS; data: ReviewerDto[] }
    | { type: typeof actionTypes.SUBMIT_FOR_APPROVAL_SUCCESS };

const initialState: PriceSimulationReducerState = {
    packages: null,
    packageDetailsMap: {},
    packageMetadataMap: {},
    reviewers: [],
};

export default function PriceSimulationReducer(state: PriceSimulationReducerState = initialState, action: PriceSimulationAction): PriceSimulationReducerState {
    const newState: PriceSimulationReducerState = { ...state };

    switch (action.type) {
        case actionTypes.STORE_PRICE_SIMULATION_PACKAGES:
            newState.packages = action.data;
            break;
        case actionTypes.STORE_PRICE_SIMULATION_PACKAGE_CONTEXTS: {
            const details = { ...newState.packageDetailsMap };
            const metadata = { ...newState.packageMetadataMap };
            action.contexts.forEach(({ package: pkg, hasActiveProposal, isOutdatedVersion }) => {
                if (pkg?.packageId === undefined) return;
                details[pkg.packageId] = roundPackagePrices(pkg);
                metadata[pkg.packageId] = { hasActiveProposal: hasActiveProposal ?? false, isOutdatedVersion: isOutdatedVersion ?? false };
            });
            newState.packageDetailsMap = details;
            newState.packageMetadataMap = metadata;
            break;
        }
        case actionTypes.SAVE_DRAFT_PROPOSALS: {
            const updatedMetadata = { ...newState.packageMetadataMap };
            action.packages.forEach((pkg) => {
                updatedMetadata[pkg.packageId] = {
                    ...updatedMetadata[pkg.packageId],
                    hasActiveProposal: true,
                };
            });
            newState.packageMetadataMap = updatedMetadata;
            break;
        }
        case actionTypes.INVALIDATE_PACKAGE_CONTEXTS: {
            const updated = { ...newState.packageDetailsMap };
            action.packageIds.forEach((id) => {
                delete updated[id];
            });
            newState.packageDetailsMap = updated;
            break;
        }
        case actionTypes.STORE_REVIEWERS:
            newState.reviewers = action.data;
            break;
        case actionTypes.SUBMIT_FOR_APPROVAL_SUCCESS:
            newState.packageDetailsMap = {};
            newState.packageMetadataMap = {};
            break;
        default:
            return state;
    }

    return newState;
}
